import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Sparkles, Play } from 'lucide-react';
import BirdMascot from './BirdMascot';
import BirdAvatar from './BirdAvatar';
import useGameStore from '../store/gameStore';

const WelcomeBackModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { nickname, avatarId, totalScore, gamesPlayed } = useGameStore();

  const handleStartGame = () => {
    onClose();
    navigate('/game');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', duration: 0.6 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md"
      >
        <Card className="p-6 sm:p-8 bg-gradient-card border-primary/30 shadow-2xl">
          {/* Birds - with player's avatar in the center */}
          <div className="flex justify-center items-center gap-3 mb-6">
            <BirdMascot size={50} color="#10B981" />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
            >
              <BirdAvatar avatarId={avatarId || 1} size={70} animate={true} />
            </motion.div>
            <BirdMascot size={50} color="#F59E0B" />
          </div>

          {/* Welcome Message */}
          <div className="text-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-4"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-primary font-semibold">Welcome Back!</span>
              <Sparkles className="w-4 h-4 text-primary" />
            </motion.div>
            
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-2">
              Hey, <span className="text-gradient-primary">{nickname}</span>!
            </h2>
            <p className="text-lg text-muted-foreground">
              Ready for another round of detective work?
            </p>
          </div>

          {/* Stats */}
          {gamesPlayed > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <div className="text-2xl font-heading font-bold text-gradient-primary">{totalScore}</div>
                <div className="text-xs text-muted-foreground">Total Score</div>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 text-center">
                <div className="text-2xl font-heading font-bold">{gamesPlayed}</div>
                <div className="text-xs text-muted-foreground">Games Played</div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              size="lg"
              onClick={handleStartGame}
              className="w-full bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
            >
              <Play className="w-5 h-5 mr-2" />
              Start Playing
            </Button>
            
            <Button
              size="lg"
              variant="ghost"
              onClick={onClose}
              className="w-full"
            >
              Maybe Later
            </Button>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default WelcomeBackModal;
